import type { SupabaseClient } from "@supabase/supabase-js";

import {
  COLOR_TOKEN_DEFAULT,
  CREATE_SHIFT_TEMPLATE_RPC,
  DISCARD_SHIFT_TEMPLATE_RPC,
  OPEN_FILL_MODE_DEFAULT,
  RETIRE_SHIFT_TEMPLATE_RPC,
  UPDATE_SHIFT_TEMPLATE_RPC,
  asColorToken,
  asOpenFillMode,
  type ColorToken,
  type OpenFillMode,
} from "../../../../packages/shared/src/schedule-color.ts";

// Template read model + create/edit write contracts (CONSOLE-SCHEDULE-TEMPLATE-CREATE/
// EDIT-LIVE). RLS: shift_templates is schedule.view-gated server-side; writes go ONLY
// through Codex's hosted create_shift_template / update_shift_template RPCs (draft-only,
// schedule.manage, no publish side effects, no slot creation) - Wave 6A additionally
// revoked INSERT/UPDATE/DELETE on the table from `authenticated`. Retired templates are
// excluded from reads - the publish RPC rejects them, so the picker never offers one.
//
// Wave 6B adds `color_token` + `open_fill_mode` (migration 20260808120000). Both write RPCs
// take the two optional params; null means "use the default" on create and "leave unchanged"
// on update, but this form always holds a concrete value and always sends both.

export interface ShiftTemplateRow {
  templateId: string;
  name: string;
  siteId: string | null;
  routeId: string | null;
  siteTimezone: string;
  localStartTime: string;
  durationMinutes: number;
  /** Postgres dow convention: 0 = Sunday .. 6 = Saturday (matches the publish expansion). */
  weekdays: number[];
  headcountRequired: number;
  status: string;
  activeFrom: string | null;
  activeUntil: string | null;
  requiredCredentialTypes: string[];
  /** Wave 6A template palette token; `slate` on a pre-6A server or an unknown value. */
  colorToken: ColorToken;
  /** Wave 6A fill policy; `instant_claim` on a pre-6A server or an unknown value. */
  openFillMode: OpenFillMode;
}

/** Columns-safety allowlist - the picker selects exactly these, nothing more. */
export const SHIFT_TEMPLATE_COLUMNS =
  "id, name, route_id, site_id, site_timezone, local_start_time, duration_minutes, weekdays, headcount_required, status, active_from, active_until, required_credential_types";

/** The 6B select list. Tried first; a pre-6A server answers 42703 and we fall back. */
export const SHIFT_TEMPLATE_COLUMNS_6B = `${SHIFT_TEMPLATE_COLUMNS}, color_token, open_fill_mode`;

/** PostgREST/PostgreSQL undefined_column - the only signal that 6A has not landed here. */
const UNDEFINED_COLUMN = "42703";

export const SCHEDULE_COLORS_UNAVAILABLE =
  "Template colors and fill modes are not available on this server yet.";

export interface ShiftTemplatesLoad {
  readonly templates: readonly ShiftTemplateRow[];
  /** True when the server predates 6A: colors shown as slate, modes as instant claim. */
  readonly degraded: boolean;
}

const TEMPLATE_LIMIT = 100;

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function isUndefinedColumn(error: unknown): boolean {
  if (error === null || typeof error !== "object") return false;
  return (error as { code?: unknown }).code === UNDEFINED_COLUMN;
}

function mapTemplateRows(data: unknown): ShiftTemplateRow[] {
  const rows = Array.isArray(data) ? (data as Array<Record<string, unknown>>) : [];
  const templates: ShiftTemplateRow[] = [];
  for (const raw of rows) {
    const templateId = asString(raw.id);
    if (templateId === null) {
      continue;
    }
    templates.push({
      templateId,
      name: asString(raw.name) ?? "Template",
      siteId: asString(raw.site_id),
      routeId: asString(raw.route_id),
      siteTimezone: asString(raw.site_timezone) ?? "UTC",
      localStartTime: asString(raw.local_start_time) ?? "",
      durationMinutes: typeof raw.duration_minutes === "number" ? raw.duration_minutes : 0,
      weekdays: Array.isArray(raw.weekdays) ? raw.weekdays.filter((d): d is number => typeof d === "number") : [],
      headcountRequired: typeof raw.headcount_required === "number" ? raw.headcount_required : 1,
      status: asString(raw.status) ?? "draft",
      activeFrom: asString(raw.active_from),
      activeUntil: asString(raw.active_until),
      requiredCredentialTypes: Array.isArray(raw.required_credential_types)
        ? raw.required_credential_types.filter((value): value is string => typeof value === "string")
        : [],
      // Unknown token -> slate, unknown mode -> instant claim. The raw string is dropped
      // here and never reaches the DOM as a color.
      colorToken: asColorToken(raw.color_token),
      openFillMode: asOpenFillMode(raw.open_fill_mode),
    });
  }
  return templates;
}

/** The default read: what the Builder and pickers may use (retired stays excluded). */
const PUBLISHABLE_STATUSES = ["draft", "published"] as const;

/** Console shell v3 lock 8 (Call 4): the Templates LIST alone may read retired history. */
const ALL_TEMPLATE_STATUSES = ["draft", "published", "retired"] as const;

export interface FetchShiftTemplatesOptions {
  /**
   * Include `retired` rows in the read. Templates-list only (2026-08-15 shell v3 lock 8):
   * retired templates stay reachable behind the "Include retired" filter, read only,
   * because invisible history reads as deletion. Pickers and the Builder keep the default
   * draft+published read per the 2026-08-15 delete/retire lock.
   */
  includeRetired?: boolean;
}

function selectTemplates(
  client: SupabaseClient,
  accountId: string,
  columns: string,
  statuses: readonly string[],
) {
  return client
    .from("shift_templates")
    .select(columns)
    .eq("account_id", accountId)
    .in("status", [...statuses])
    .order("name", { ascending: true })
    .limit(TEMPLATE_LIMIT);
}

/**
 * Templates for the account, by name - draft + published by default, plus retired history
 * when the Templates list asks for it. Throws safe.
 *
 * Probe-then-fall-back: the extended select is tried first, and a `42703` (undefined column)
 * means this server predates Wave 6A. The picker then re-reads the legacy column list and
 * reports `degraded` rather than going blank - losing colors is not a reason to lose the
 * surface that works today.
 */
export async function fetchShiftTemplates(
  client: SupabaseClient,
  accountId: string,
  options: FetchShiftTemplatesOptions = {},
): Promise<ShiftTemplatesLoad> {
  const statuses = options.includeRetired === true ? ALL_TEMPLATE_STATUSES : PUBLISHABLE_STATUSES;
  const extended = await selectTemplates(client, accountId, SHIFT_TEMPLATE_COLUMNS_6B, statuses);
  if (extended.error === null && Array.isArray(extended.data)) {
    return { templates: mapTemplateRows(extended.data), degraded: false };
  }
  if (!isUndefinedColumn(extended.error)) {
    throw new Error("Could not load shift templates.");
  }
  const legacy = await selectTemplates(client, accountId, SHIFT_TEMPLATE_COLUMNS, statuses);
  if (legacy.error !== null || !Array.isArray(legacy.data)) {
    throw new Error("Could not load shift templates.");
  }
  return { templates: mapTemplateRows(legacy.data), degraded: true };
}

export interface ShiftTemplateStatusCounts {
  active: number;
  draft: number;
  retired: number;
}

async function countTemplatesWithStatus(
  client: SupabaseClient,
  accountId: string,
  status: string,
): Promise<number> {
  const result = await client
    .from("shift_templates")
    .select("id", { count: "exact", head: true })
    .eq("account_id", accountId)
    .eq("status", status);
  if (result.error !== null || typeof result.count !== "number") {
    throw new Error("Could not load shift templates.");
  }
  return result.count;
}

/**
 * Status tally for the Schedule workbench metrics strip (shell v3, board rule 9: honest
 * numbers in the first viewport). Three head-only exact counts over the same existing
 * status column the list reads - no rows transferred, no row cap to lie past, and no new
 * server surface.
 */
export async function fetchShiftTemplateStatusCounts(
  client: SupabaseClient,
  accountId: string,
): Promise<ShiftTemplateStatusCounts> {
  const [active, draft, retired] = await Promise.all([
    countTemplatesWithStatus(client, accountId, "published"),
    countTemplatesWithStatus(client, accountId, "draft"),
    countTemplatesWithStatus(client, accountId, "retired"),
  ]);
  return { active, draft, retired };
}

// --- Writes (hosted create/update RPCs) ---------------------------------------------------

/** The full editable shape both write RPCs take - drafts only, publishing stays separate. */
export interface ShiftTemplateDraft {
  siteId: string;
  routeId: string;
  name: string;
  siteTimezone: string;
  localStartTime: string;
  durationMinutes: number;
  weekdays: number[];
  activeFrom: string;
  activeUntil: string | null;
  headcountRequired: number;
  requiredCredentialTypes: string[];
  colorToken: ColorToken;
  openFillMode: OpenFillMode;
}

/** Every field a fresh draft starts from, so create and edit share one shape. */
export const EMPTY_TEMPLATE_COLOR: ColorToken = COLOR_TOKEN_DEFAULT;
export const EMPTY_TEMPLATE_OPEN_FILL_MODE: OpenFillMode = OPEN_FILL_MODE_DEFAULT;

export const SAFE_TEMPLATE_WRITE_FAILURE = "Couldn't save that template — please try again.";

// Contract-stable, value-free validation sentences from both hosted RPCs (25525b2 +
// 63a1c00, extended by Wave 6A migration 20260808120000). EXACT matches render verbatim;
// anything else fails closed. Sentences are copied from the shipped migration, not the
// design draft - the two color/mode sentences are ADDED to this set, never forked into a
// second one.
export const KNOWN_TEMPLATE_WRITE_FAILURES: ReadonlySet<string> = new Set([
  "Caller is not authorized to manage schedules for this account.",
  "Site and route are required to create schedule templates.",
  "Site and route are required to update schedule templates.",
  "Template name is required.",
  "Site timezone is required.",
  "Local start time is required.",
  "Duration minutes is required.",
  "Template weekdays are required.",
  "Active-from date is required.",
  "Headcount is required.",
  "Site was not found for this account.",
  "Route was not found for this account and site.",
  "Shift template was not found for this account.",
  "Only draft schedule templates can be updated.",
  // Wave 6A additions.
  "Authentication is required to create schedule templates.",
  "Authentication is required to update schedule templates.",
  "Account id is required to create schedule templates.",
  "Account id is required to update schedule templates.",
  "Active account membership is required.",
  "Shift template id is required to update schedule templates.",
  "Shift template color token is not supported.",
  "Shift template open fill mode is not supported.",
]);

/** The contract's sentence for a mode the enum does not carry. */
export const OPEN_FILL_MODE_UNSUPPORTED = "Shift template open fill mode is not supported.";

/**
 * PostgreSQL rejects an invalid `public.open_fill_mode` literal with SQLSTATE 22P02 BEFORE
 * the function body runs, so no verbatim sentence comes back (6A contract, Typed params).
 * Only a 22P02 that names the enum maps to the mode sentence; a 22P02 from anywhere else
 * takes the safe sentence, because this surface must not become an error oracle.
 */
export function isOpenFillModeEnumRejection(error: unknown): boolean {
  if (error === null || typeof error !== "object") return false;
  if ((error as { code?: unknown }).code !== "22P02") return false;
  const message = (error as { message?: unknown }).message;
  if (typeof message !== "string") return false;
  // Require the enum-rejection SHAPE, not just the type name: a 22P02 that merely quotes
  // "open_fill_mode" as some other malformed value is not a rejected mode literal.
  const lowered = message.toLowerCase();
  return lowered.includes("invalid input value for enum") && lowered.includes("open_fill_mode");
}

export type TemplateWriteOutcome = { ok: true; templateId: string } | { ok: false; reason: string };

function draftParams(accountId: string, draft: ShiftTemplateDraft): Record<string, unknown> {
  return {
    p_account_id: accountId,
    p_site_id: draft.siteId,
    p_route_id: draft.routeId,
    p_name: draft.name,
    p_site_timezone: draft.siteTimezone,
    p_local_start_time: draft.localStartTime,
    p_duration_minutes: draft.durationMinutes,
    p_weekdays: draft.weekdays,
    p_active_from: draft.activeFrom,
    p_active_until: draft.activeUntil,
    p_headcount_required: draft.headcountRequired,
    p_required_credential_types: draft.requiredCredentialTypes,
    // Always sent, never null: a supervisor who picked `teal` and got a receipt must not
    // have stored `slate`. There is deliberately NO retry-without-color path.
    p_color_token: draft.colorToken,
    p_open_fill_mode: draft.openFillMode,
  };
}

async function runTemplateWrite(
  client: SupabaseClient,
  fn: string,
  params: Record<string, unknown>,
): Promise<TemplateWriteOutcome> {
  try {
    const { data, error } = await client.rpc(fn, params);
    if (error !== null) {
      if (isOpenFillModeEnumRejection(error)) {
        return { ok: false, reason: OPEN_FILL_MODE_UNSUPPORTED };
      }
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_TEMPLATE_WRITE_FAILURES.has(message) ? message : SAFE_TEMPLATE_WRITE_FAILURE,
      };
    }
    const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
    if (row === undefined || typeof row.shift_template_id !== "string") {
      return { ok: false, reason: SAFE_TEMPLATE_WRITE_FAILURE };
    }
    return { ok: true, templateId: row.shift_template_id };
  } catch {
    return { ok: false, reason: SAFE_TEMPLATE_WRITE_FAILURE };
  }
}

/** Create a DRAFT template (no publish, no slots). Never throws. */
export async function createShiftTemplate(
  client: SupabaseClient,
  accountId: string,
  draft: ShiftTemplateDraft,
): Promise<TemplateWriteOutcome> {
  return runTemplateWrite(client, CREATE_SHIFT_TEMPLATE_RPC, draftParams(accountId, draft));
}

/** Update a DRAFT template (published/retired refused server-side). Never throws. */
export async function updateShiftTemplate(
  client: SupabaseClient,
  accountId: string,
  shiftTemplateId: string,
  draft: ShiftTemplateDraft,
): Promise<TemplateWriteOutcome> {
  return runTemplateWrite(client, UPDATE_SHIFT_TEMPLATE_RPC, {
    p_shift_template_id: shiftTemplateId,
    ...draftParams(accountId, draft),
  });
}

// --- Lifecycle (discard draft / retire published) ------------------------------------------
//
// Migration 20260815120000. Both are `schedule.manage`-gated server-side and both are
// confirm-gated in the UI. Discard HARD-DELETES a never-published draft that nothing
// references; retire flips a published template to `retired` and touches no slot. The
// server owns every refusal - this layer only decides which sentences are safe to render.
//
// Retired templates fall out of `fetchShiftTemplates` (it selects draft + published), so
// both surfaces converge on the same refresh: reload and the row is either gone or hidden.

export const SAFE_TEMPLATE_DISCARD_FAILURE =
  "Couldn't discard that draft template — please try again.";
export const SAFE_TEMPLATE_RETIRE_FAILURE =
  "Couldn't retire that template — please try again.";

/**
 * EXACT sentences from the shipped migration, copied verbatim. Anything else falls back
 * to the safe sentence so this surface never becomes an error oracle.
 */
export const KNOWN_TEMPLATE_DISCARD_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to discard schedule templates.",
  "Account id is required to discard schedule templates.",
  "Shift template id is required to discard schedule templates.",
  "Active account membership is required.",
  "Caller is not authorized to manage schedules for this account.",
  "Shift template was not found for this account.",
  "Only draft schedule templates can be discarded.",
  "Previously published schedule templates cannot be discarded.",
  "Schedule templates with shift slots cannot be discarded.",
  "Schedule templates linked to a shift channel cannot be discarded.",
  "Schedule templates linked to open shift requests cannot be discarded.",
]);

export const KNOWN_TEMPLATE_RETIRE_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to retire schedule templates.",
  "Account id is required to retire schedule templates.",
  "Shift template id is required to retire schedule templates.",
  "Active account membership is required.",
  "Caller is not authorized to manage schedules for this account.",
  "Shift template was not found for this account.",
  "Only published schedule templates can be retired.",
]);

export type TemplateDiscardOutcome = { ok: true } | { ok: false; reason: string };

/** `retainedSlotCount` is the count of slots the retire deliberately LEFT on the calendar. */
export type TemplateRetireOutcome =
  | { ok: true; retainedSlotCount: number }
  | { ok: false; reason: string };

/** Hard-delete a never-published draft template. Never throws. */
export async function discardShiftTemplate(
  client: SupabaseClient,
  accountId: string,
  shiftTemplateId: string,
): Promise<TemplateDiscardOutcome> {
  try {
    const { data, error } = await client.rpc(DISCARD_SHIFT_TEMPLATE_RPC, {
      p_account_id: accountId,
      p_shift_template_id: shiftTemplateId,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_TEMPLATE_DISCARD_FAILURES.has(message)
          ? message
          : SAFE_TEMPLATE_DISCARD_FAILURE,
      };
    }
    const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
    // Only the server's own `discarded` receipt counts as success - a shape we do not
    // recognise is reported as a failure rather than claimed as a delete.
    if (row === undefined || row.discarded !== true) {
      return { ok: false, reason: SAFE_TEMPLATE_DISCARD_FAILURE };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: SAFE_TEMPLATE_DISCARD_FAILURE };
  }
}

/** Retire a published template. Slots stay; the RPC is idempotent. Never throws. */
export async function retireShiftTemplate(
  client: SupabaseClient,
  accountId: string,
  shiftTemplateId: string,
): Promise<TemplateRetireOutcome> {
  try {
    const { data, error } = await client.rpc(RETIRE_SHIFT_TEMPLATE_RPC, {
      p_account_id: accountId,
      p_shift_template_id: shiftTemplateId,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_TEMPLATE_RETIRE_FAILURES.has(message)
          ? message
          : SAFE_TEMPLATE_RETIRE_FAILURE,
      };
    }
    const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
    if (row === undefined || row.template_status !== "retired") {
      return { ok: false, reason: SAFE_TEMPLATE_RETIRE_FAILURE };
    }
    return {
      ok: true,
      retainedSlotCount:
        typeof row.retained_slot_count === "number" ? row.retained_slot_count : 0,
    };
  } catch {
    return { ok: false, reason: SAFE_TEMPLATE_RETIRE_FAILURE };
  }
}
